import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ClientListInterface,
  CompaniesListInterface,
  EmployeeListInterface,
  FamilyListInterface,
  FeePlanListInterface,
  PartnerListInterface,
  PortfolioGroupListInterface,
  PortfolioListInterface,
} from "interfaces/PrivateRouteInterface";
import { OnboardingInterface } from "interfaces/SettingsInterface";

interface TabInterface {
  familyERMPageTabs: number;
  companyInfoTabs: number;
  defaultPermissionTabs: number;
  modelPortfolioTabs: number;
  employeeERMTabs: number;
  portfolioSummaryTabs: number;
  portfolioUtilityTabs: number;
  portfolioReportTabs: number;
  settingsPageTabs: number;
  investorBasketTabs: number;
  tabValueOfCRM: number;
  tabValueOfRevenue: number;
  tabOfPortfolioRevenue: number;
  tabValueDashboard: number;
  tabValueOfImport: number;
  tabValueOfReports: number;
  editFeePlanTab: number;
  complianceTabs: number;
  clientERMPageTabs: number;
  leadDetailsTabs: number;
  portfolioExtendedViewTabs: number;
  portfolioTabs: number;
  portfolioAdminViewTabs: number;
  assetDetailsTabs: number;
  portfolioProfileTab: number;
  organisationTab: number;
  partnerDetailsPageTab: number;
  manageEquityBasketTab: number;
}

export type PreviewMode = "desktop" | "tablet" | "mobile";

interface AppState {
  isFirstLogin: boolean;
  sidebarOpen: boolean;
  documentTitle: string;
  tab: TabInterface;
  noteFilter: number[];
  adviseRegisterFilter: number[];
  portfolios: any[];
  companies: CompaniesListInterface[];
  portfolioGroupList: any[];
  clientList: any[];
  employeeList: any[];
  familyList: any[];
  countryList: any[];
  stateList: any[];
  cityList: any[];
  selectedCountry: number;
  selectedState: number;
  selectedCity: number;
  settingTab: {
    type: string;
    name: string;
  };
  partnerList: PartnerListInterface[];
  onBoardflowData: OnboardingInterface;
  feePlanList: FeePlanListInterface[];
  investorOnBoardflowData: OnboardingInterface;
  // tag: TagInterface[];
  proformaState: any[];
}

const initialState: AppState = {
  isFirstLogin: false,
  sidebarOpen: JSON.parse(localStorage.getItem("sidebar")),
  documentTitle: "Dashboard",
  tab: {
    familyERMPageTabs: 0,
    companyInfoTabs: 0,
    leadDetailsTabs: 0,
    defaultPermissionTabs: 0,
    modelPortfolioTabs: 0,
    employeeERMTabs: 0,
    portfolioSummaryTabs: 0,
    portfolioUtilityTabs: 0,
    portfolioReportTabs: 0,
    settingsPageTabs: 0,
    tabValueOfCRM: 0,
    tabValueOfRevenue: 0,
    tabOfPortfolioRevenue: 0,
    tabValueDashboard: 0,
    tabValueOfImport: 0,
    tabValueOfReports: 0,
    editFeePlanTab: 0,
    complianceTabs: 0,
    clientERMPageTabs: 0,
    investorBasketTabs: 0,
    portfolioTabs: 0,
    portfolioExtendedViewTabs: 1,
    portfolioAdminViewTabs: 0,
    assetDetailsTabs: 0,
    portfolioProfileTab: 0,
    organisationTab: 0,
    partnerDetailsPageTab: 0,
    manageEquityBasketTab: 0,
  },
  noteFilter: [],
  adviseRegisterFilter: [],
  portfolios: [],
  companies: [],
  portfolioGroupList: [],
  clientList: [],
  employeeList: [],
  countryList: [],
  stateList: [],
  cityList: [],
  familyList: [],
  selectedCountry: null,
  selectedState: null,
  selectedCity: null,
  settingTab: {
    type: "USER_PROFILE",
    name: "User Profile",
  },
  partnerList: [],
  onBoardflowData: null,
  feePlanList: [],
  investorOnBoardflowData: null,
  // tag: null,
  proformaState: [],
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    SIDE_BAR_OPEN: (state, action) => {
      state.sidebarOpen = action.payload;
      localStorage.setItem("sidebar", JSON.stringify(action.payload));
    },
    SETTING_TAB: (state, action) => {
      state.settingTab = action.payload;
    },
    DOCUMENT_TITLE: (state, action) => {
      state.documentTitle = action.payload;
    },
    IS_FIRST_LOGIN: (state, action) => {
      state.isFirstLogin = action.payload;
    },
    NOTE_FILTER: (state, action) => {
      state.noteFilter = action.payload;
    },
    ADVICE_REGISTER_FILTER: (state, action) => {
      state.adviseRegisterFilter = action.payload;
    },
    PORTFOLIOS: (state, action) => {
      state.portfolios = action.payload.map((item: PortfolioListInterface) => ({
        ...item,
        name: `${item.name} - ${item.portfolioId}`,
        value: item.id,
        portfolioName: item.name,
      }));
    },
    COMPANIES: (state, action) => {
      state.companies = action.payload?.map((item: CompaniesListInterface) => ({
        ...item,
        name: item.name,
        value: item.gridCode,
      }));
    },
    PORTFOLIO_GROUP_LIST: (state, action) => {
      state.portfolioGroupList = action.payload?.map(
        (item: PortfolioGroupListInterface) => ({
          name: item.name,
          value: item.id,
        }),
      );
    },
    CLIENT_LIST: (state, action) => {
      state.clientList = action.payload?.map((item: ClientListInterface) => ({
        ...item,
        name: `${item.name} - ${item?.clientCode}`,
        value: item.id,
      }));
    },
    EMPLOYEE_LIST: (state, action) => {
      state.employeeList = action.payload?.map(
        (item: EmployeeListInterface) => ({
          name: `${item.name || "-"}`,
          value: item.id,
        }),
      );
    },
    FAMILY_LIST: (state, action) => {
      state.familyList = action.payload?.map((family: FamilyListInterface) => ({
        ...family,
        name: family.name,
        value: family.id,
      }));
    },
    PARTNER_LIST: (state, action) => {
      state.partnerList = action.payload?.map((item: PartnerListInterface) => ({
        ...item,
        name: item.name,
        value: item.id,
      }));
    },
    FEE_PLAN_LIST: (state, action) => {
      state.feePlanList = action.payload?.map(
        (dataItem: FeePlanListInterface) => {
          return {
            name: dataItem.name,
            value: dataItem.value,
          };
        },
      );
    },
    ON_BOARDING_FLOW: (state, action) => {
      state.onBoardflowData = action.payload;
    },
    INVESTOR_ON_BOARDING_FLOW: (state, action) => {
      state.investorOnBoardflowData = action.payload;
    },
    TAB: (
      state,
      action: PayloadAction<{
        tabKey: keyof TabInterface;
        tabValue: TabInterface[keyof TabInterface];
      }>,
    ) => {
      const { tabKey, tabValue } = action.payload;
      state.tab[tabKey] = tabValue;
    },
    COUNTRY_LIST: (state, action) => {
      state.countryList = action.payload;
    },
    STATE_LIST: (state, action) => {
      state.stateList = action.payload;
    },
    CITY_LIST: (state, action) => {
      state.cityList = action.payload;
    },
    SELECTED_COUNTRY: (state, action) => {
      state.selectedCountry = action.payload;
    },
    SELECTED_STATE: (state, action) => {
      state.selectedState = action.payload;
    },
    SELECTED_CITY: (state, action) => {
      state.selectedCity = action.payload;
    },
    // TAG: (state, action) => {
    //   state.tag = action.payload?.map((dataItem: TagInterface) => {
    //     return {
    //       ...dataItem,
    //       name: dataItem.name,
    //       value: dataItem.id,
    //     };
    //   });
    // },
    proformaStateList: (state, action) => {
      state.proformaState = action.payload;
    },
  },
});

export const {
  SIDE_BAR_OPEN,
  SETTING_TAB,
  DOCUMENT_TITLE,
  IS_FIRST_LOGIN,
  NOTE_FILTER,
  ADVICE_REGISTER_FILTER,
  PORTFOLIOS,
  COMPANIES,
  PORTFOLIO_GROUP_LIST,
  CLIENT_LIST,
  EMPLOYEE_LIST,
  FAMILY_LIST,
  PARTNER_LIST,
  FEE_PLAN_LIST,
  ON_BOARDING_FLOW,
  INVESTOR_ON_BOARDING_FLOW,
  TAB,
  COUNTRY_LIST,
  STATE_LIST,
  CITY_LIST,
  SELECTED_COUNTRY,
  SELECTED_STATE,
  SELECTED_CITY,
  // TAG,
  proformaStateList,
} = appSlice.actions;

export default appSlice.reducer;
